import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiCheck, BiChevronDown, BiChevronUp, BiInfoCircle, BiLink } from 'react-icons/bi';

import { IGetAllScenariosResponse } from '@/app/services/chatbot-rtk.type';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

type selected = {
  bot_id: string;
  name: string;
  type: string;
};
interface EnhancedBotDependencyProps {
  item: IGetAllScenariosResponse;
  source: IGetAllScenariosResponse[] | undefined;
  selected: selected[];
}

const EnhancedBotDependency: React.FC<EnhancedBotDependencyProps> = ({ item, source, selected }) => {
  const { t } = useTranslation();
  if (!item?.dependencies?.dependsOn || item.dependencies.dependsOn.length === 0) {
    return null;
  }

  const dependencies = item.dependencies.dependsOn
    .map(depId => {
      const depBot = source?.find(b => b.id === depId);
      if (depBot) {
        return {
          bot_id: depBot.id,
          name: depBot.title as string,
          type: depBot.type,
          data: depBot,
        };
      }
      return null;
    })
    .filter(Boolean) as Array<{
    bot_id: string;
    name: string;
    type: string;
    data: IGetAllScenariosResponse;
  }>;

  const [isExpanded, setIsExpanded] = useState(dependencies.length > 0);

  const getBotIcon = (bot: IGetAllScenariosResponse) => {
    if (bot.type === BotTypeEnum.VIDEO_SCENARIO && bot.icon) {
      const icon = bot.icon;
      if (icon.type === 'image') {
        return <img src={icon.value} alt={bot.title} className="tw-w-6 tw-h-6 tw-rounded-full tw-object-cover" />;
      }
    }
    return (
      <img
        src={bot.logo || 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png'}
        alt={bot.title}
        className="tw-w-6 tw-h-6 tw-rounded-full tw-object-cover"
      />
    );
  };

  return (
    <div className="tw-space-y-3">
      {/* Dependency Indicator */}
      <div>
        <button
          id="btn-dependency"
          type="button"
          onClick={e => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg hover:tw-bg-blue-100 tw-transition-colors tw-text-sm"
        >
          <BiLink className="tw-w-4 tw-h-4 tw-text-blue-600" />
          <span className="tw-font-medium tw-text-blue-700 tw-font-['Lexend']">
            {dependencies.length} {dependencies.length === 1 ? t('marketplace/step2/dependency') : t('marketplace/step2/dependencies/title')}
          </span>
          {isExpanded ? <BiChevronUp className="tw-w-4 tw-h-4 tw-text-blue-600" /> : <BiChevronDown className="tw-w-4 tw-h-4 tw-text-blue-600" />}
        </button>
      </div>

      {/* Expanded Dependencies List */}
      {isExpanded && (
        <div className="tw-ml-4 tw-pl-3 tw-border-l-2 tw-border-blue-200 tw-space-y-2">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <BiInfoCircle className="tw-w-4 tw-h-4 tw-text-blue-500" />
            <span className="tw-text-xs tw-text-blue-600 tw-font-['Lexend']">{t('marketplace/step2/dependencies/required')}</span>
          </div>

          {dependencies?.map(dep => {
            const isSelected = selected.some(item => item.bot_id === dep.bot_id);
            return (
              <div key={dep.bot_id} className="tw-flex tw-items-center tw-gap-3 tw-p-2 tw-bg-gray-50 tw-rounded-lg tw-pointer-events-none">
                {getBotIcon(dep.data)}
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-font-medium tw-text-gray-900 tw-font-['Lexend'] tw-text-xs tw-truncate">{dep.name}</div>
                  <div className="tw-text-xs tw-text-gray-500 tw-font-['Lexend']">{Utilities.getBotNameByBotCode(dep.type, t)}</div>
                </div>
                {isSelected && <BiCheck size={22} className="tw-text-green-500" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WrappedErrorBoundary(EnhancedBotDependency);
